import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  LogOut,
  Package,
  Pencil,
  Plus,
  Receipt,
  Settings2,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { API } from "../lib/api";
import { CATEGORIES } from "../data/site";
import { COLOUR_OPTIONS } from "../lib/colours";

const TOKEN_KEY = "sparkwear_admin_token";

const EMPTY_FORM = {
  name: "",
  category_id: "kids-clothing",
  price: "",
  stock: "",
  compare_at: "",
  tagline: "",
  badge: "",
  sizes: "One Size",
  colours: [],
};

const STATUS_STYLES = {
  DEMO_PAID: "bg-butter-50 text-ink",
  COMPLETED: "bg-mint-50 text-mint-600",
  APPROVED: "bg-mint-50 text-mint-600",
  CREATED: "bg-lav-50 text-lav-700",
  VOIDED: "bg-blush-50 text-blush-700",
};

const DELIVERY_LABELS = {
  confirmed: "Confirmed",
  packed: "Packed",
  shipped: "Shipped",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
};
const DELIVERY_STEPS = ["packed", "shipped", "out_for_delivery", "delivered"];

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("products");
  const [passcode, setPasscode] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photos, setPhotos] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({ count: 0, revenue: 0 });
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [passForm, setPassForm] = useState({ current: "", next: "" });
  const [changingPass, setChangingPass] = useState(false);
  const [aiWriting, setAiWriting] = useState(false);
  const fileRef = useRef(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const [statusBusy, setStatusBusy] = useState("");
  const [trackingDraft, setTrackingDraft] = useState({});

  const updateOrderStatus = async (o, status) => {
    setStatusBusy(o.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${o.id}/status`,
        { delivery_status: status, tracking_number: trackingDraft[o.id]?.trim() || null },
        authHeaders()
      );
      setOrders((prev) =>
        prev.map((x) =>
          x.id === o.id
            ? {
                ...x,
                delivery_status: data.delivery_status,
                tracking_number: data.tracking_number,
                status_history: [...(x.status_history || []), { status, at: new Date().toISOString() }],
              }
            : x
        )
      );
      toast.success(`${o.id} → ${DELIVERY_LABELS[status]} — buyer emailed automatically`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the order.");
    } finally {
      setStatusBusy("");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setTab("products");
    setForm(EMPTY_FORM);
    setEditingId(null);
    setPhotos([]);
    setPreview("");
  };

  const loadProducts = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/admin/products`, authHeaders());
      setProducts(res.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Could not load your products.");
    } finally {
      setLoadingList(false);
    }
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, authHeaders());
      setOrders(res.data.orders || []);
      setOrderStats({ count: res.data.count || 0, revenue: res.data.revenue || 0 });
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Could not load your orders.");
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    if (token) loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token && tab === "orders") loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const login = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong passcode.");
    } finally {
      setLoggingIn(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, fd, authHeaders());
      setPhotos((prev) => [...prev, res.data.url]);
      toast.success("Photo added to gallery.");
    } catch (err) {
      setPreview("");
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const describeProduct = async () => {
    if (aiWriting || photos.length === 0) {
      if (photos.length === 0) toast.error("Upload a photo first so the AI can see it.");
      return;
    }
    setAiWriting(true);
    try {
      const res = await axios.post(
        `${API}/admin/describe`,
        { name: form.name, image: photos[0] },
        authHeaders()
      );
      setForm((f) => ({
        ...f,
        tagline: res.data.description || res.data.tagline || f.tagline,
        badge: f.badge.trim() || res.data.badge || "",
      }));
      toast.success("Description written — tweak it if you like!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The AI writer is busy — please try again.");
    } finally {
      setAiWriting(false);
    }
  };

  const removePhoto = (index) => {
    if (photos.length <= 1) return toast.error("Keep at least one photo.");
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name.trim()) return toast.error("Give your product a name.");
    if (!form.price || Number(form.price) <= 0) return toast.error("Set a price in pounds.");
    if (photos.length === 0) return toast.error("Upload a product photo first.");
    setSaving(true);
    const payload = {
      name: form.name,
      category_id: form.category_id,
      tagline: form.tagline,
      price: Number(form.price),
      stock: form.stock === "" ? null : Math.max(0, Math.round(Number(form.stock))),
      compare_at: form.compare_at === "" ? null : Number(form.compare_at),
      badge: form.badge.trim() || null,
      image: photos[0],
      images: photos.slice(1),
      featured: false,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      colours: form.colours,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/products/${editingId}`, payload, authHeaders());
        toast.success("Product updated.");
      } else {
        await axios.post(`${API}/admin/products`, payload, authHeaders());
        toast.success("Product added to your shop!");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setPhotos([]);
      setPreview("");
      loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the product.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      category_id: p.category_id,
      price: String(p.price),
      stock: p.stock == null ? "" : String(p.stock),
      compare_at: p.compare_at == null ? "" : String(p.compare_at),
      tagline: p.tagline || "",
      badge: p.badge || "",
      sizes: (p.sizes || []).join(", "),
      colours: p.colours || [],
    });
    setPhotos([p.image, ...(p.images || [])].filter(Boolean));
    setPreview(p.image);
    setTab("products");
    window.scrollTo(0, 0);
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from your shop?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, authHeaders());
      toast.success("Product removed.");
      loadProducts();
    } catch {
      toast.error("Could not remove the product.");
    }
  };

  const changePass = async (e) => {
    e.preventDefault();
    if (changingPass) return;
    setChangingPass(true);
    try {
      await axios.put(`${API}/admin/passcode`, passForm, authHeaders());
      toast.success("Passcode changed — use it next time you log in.");
      setPassForm({ current: "", next: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change the passcode.");
    } finally {
      setChangingPass(false);
    }
  };

  if (!token) {
    return (
      <main
        className="flex min-h-screen items-center justify-center bg-gradient-to-b from-blush-50 to-cream px-5 pb-24 pt-28"
        data-testid="admin-login-view"
      >
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onSubmit={login}
          className="w-full max-w-sm rounded-[32px] border border-lav-100 bg-white p-8 shadow-xl shadow-lav-100"
        >
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blush-50">
            <LockKeyhole className="h-6 w-6 text-blush-700" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-semibold text-ink">
            Sparkwear Studio
          </h1>
          <p className="mt-1 text-sm text-muted">
            Enter your passcode to manage your shop.
          </p>
          <div className="relative mt-6">
            <input
              type={showPass ? "text" : "password"}
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              data-testid="admin-passcode-input"
              className="w-full rounded-full border border-lav-100 bg-cream px-5 py-3.5 pr-12 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
            />
            <button
              type="button"
              onClick={() => setShowPass((v) => !v)}
              aria-label={showPass ? "Hide passcode" : "Show passcode"}
              data-testid="admin-passcode-toggle"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted"
            >
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-button"
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70"
          >
            {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
            {loggingIn ? "Checking..." : "Enter Studio"}
          </button>
        </motion.form>
      </main>
    );
  }

  return (
    <main className="min-h-screen pb-24 pt-28" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
              Sparkwear Studio
            </p>
            <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight text-ink">
              Your Shop, Your Rules
            </h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-lav-100 bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-blush-50"
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          {[
            { id: "products", label: "Products", icon: Package },
            { id: "orders", label: "Orders", icon: Receipt },
            { id: "settings", label: "Settings", icon: Settings2 },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                tab === t.id
                  ? "bg-ink text-white shadow-md shadow-ink/15"
                  : "border border-lav-100 bg-white text-muted hover:text-ink"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <form
              onSubmit={submit}
              data-testid="admin-product-form"
              className="h-fit rounded-[32px] border border-lav-100 bg-white p-7 md:p-8 lg:col-span-5"
            >
              <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-ink">
                {editingId ? <Pencil className="h-5 w-5 text-blush-600" /> : <Plus className="h-5 w-5 text-blush-600" />}
                {editingId ? "Edit product" : "Add a product"}
              </h2>

              <div className="mt-6">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">
                  Photos (first one is the main shot)
                </span>
                <div className="mt-3 flex flex-wrap gap-3">
                  {photos.map((url, i) => (
                    <div key={url + i} className="relative h-20 w-20 overflow-hidden rounded-2xl border border-lav-100">
                      <img src={url} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                      {i === 0 && (
                        <span className="absolute bottom-1 left-1 rounded-full bg-ink/80 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                          Main
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        aria-label={`Remove photo ${i + 1}`}
                        data-testid={`photo-remove-${i}`}
                        className="absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-ink shadow"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {uploading && (
                    <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-lav-300 bg-cream">
                      <Loader2 className="h-5 w-5 animate-spin text-lav-500" />
                    </div>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={onPickFile}
                  data-testid="product-image-input"
                  className="hidden"
                  id="product-image-file"
                />
                <label
                  htmlFor="product-image-file"
                  className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-full bg-blush-500 px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                  <Upload className="h-4 w-4" />
                  {photos.length === 0 ? "Upload photo" : "Add another photo"}
                </label>
                <p className="mt-2 text-xs text-muted">JPG, PNG or WEBP · up to 5MB</p>
              </div>

              <button
                type="button"
                onClick={describeProduct}
                disabled={aiWriting || photos.length === 0}
                data-testid="ai-describe-button"
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-blush-300/70 bg-blush-50 px-5 py-3 font-display text-sm font-semibold text-blush-700 transition-all duration-300 hover:scale-[1.01] hover:bg-blush-100 disabled:opacity-50"
              >
                {aiWriting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {aiWriting ? "Writing the description..." : "Write description for me"}
              </button>

              {[
                { key: "name", label: "Product name *", ph: "Sparkly Raincoat", type: "text" },
                { key: "price", label: "Price (£) *", ph: "24.99", type: "number" },
                { key: "compare_at", label: "Was price (£) — makes it a sale", ph: "34.99", type: "number" },
                { key: "stock", label: "Stock count — leave empty if not tracking", ph: "12", type: "number" },
                { key: "tagline", label: "Short description", ph: "Shower-proof fun for drizzly days", type: "text" },
                { key: "badge", label: "Badge (optional)", ph: "New", type: "text" },
                { key: "sizes", label: "Sizes (comma separated)", ph: "One Size", type: "text" },
              ].map((f) => (
                <label key={f.key} className="mt-4 block">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">{f.label}</span>
                  <input
                    type={f.type}
                    step={f.type === "number" ? "0.01" : undefined}
                    min={f.type === "number" ? "0" : undefined}
                    value={form[f.key]}
                    onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.ph}
                    data-testid={`product-form-${f.key}`}
                    className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-muted/50 focus:border-blush-500"
                  />
                </label>
              ))}

              <div className="mt-4">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">
                  Colours (tap every colour this product comes in)
                </span>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="product-colour-options">
                  {COLOUR_OPTIONS.map((c) => {
                    const on = form.colours.includes(c.name);
                    return (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            colours: on ? f.colours.filter((x) => x !== c.name) : [...f.colours, c.name],
                          }))
                        }
                        data-testid={`product-colour-chip-${c.name.toLowerCase()}`}
                        className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors duration-200 ${
                          on ? "border-blush-500 bg-blush-50 text-ink" : "border-lav-100 bg-white text-muted hover:bg-blush-50"
                        }`}
                      >
                        <span className="h-3.5 w-3.5 rounded-full border border-ink/15" style={{ backgroundColor: c.hex }} />
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="mt-4 block">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Category *</span>
                <select
                  value={form.category_id}
                  onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
                  data-testid="product-form-category"
                  className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>

              <button
                type="submit"
                disabled={saving || uploading}
                data-testid="product-save-button"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingId ? "Save changes" : "Add to shop"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setForm(EMPTY_FORM);
                    setPhotos([]);
                    setPreview("");
                  }}
                  data-testid="product-cancel-edit-button"
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-lav-100 py-3.5 font-display text-sm font-semibold text-muted transition-colors duration-200 hover:bg-blush-50"
                >
                  <X className="h-4 w-4" /> Cancel editing
                </button>
              )}
            </form>

            <div className="lg:col-span-7" data-testid="admin-product-list">
              {loadingList ? (
                <div className="space-y-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-24 animate-pulse rounded-[24px] bg-blush-50" />
                  ))}
                </div>
              ) : (
                <ul className="space-y-4">
                  {products.map((p) => (
                    <li
                      key={p.id}
                      data-testid={`admin-product-row-${p.id}`}
                      className="flex items-center gap-4 rounded-[24px] border border-lav-100 bg-white p-4"
                    >
                      <img src={p.image} alt={p.name} className="h-20 w-16 shrink-0 rounded-xl object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-display text-base font-semibold text-ink">{p.name}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                          <span className="text-muted">
                            {CATEGORIES.find((c) => c.id === p.category_id)?.name || p.category_id}
                          </span>
                          <span className="font-semibold text-ink">
                            £{p.price.toFixed(2)}
                            {p.compare_at != null && p.compare_at > p.price && (
                              <span className="ml-1.5 font-normal text-muted line-through">£{p.compare_at.toFixed(2)}</span>
                            )}
                          </span>
                          {p.stock != null && (
                            <span className={p.stock <= 0 ? "font-semibold text-blush-700" : "text-muted"}>
                              {p.stock <= 0 ? "Sold out" : `Stock: ${p.stock}`}
                            </span>
                          )}
                          {(p.images || []).length > 0 && (
                            <span className="text-muted">+{p.images.length} photo{(p.images.length > 1) ? "s" : ""}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => startEdit(p)}
                        data-testid={`product-edit-button-${p.id}`}
                        aria-label={`Edit ${p.name}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-lav-100 text-muted transition-colors duration-200 hover:bg-blush-50 hover:text-blush-700"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(p)}
                        data-testid={`product-delete-button-${p.id}`}
                        aria-label={`Remove ${p.name}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-lav-100 text-muted transition-colors duration-200 hover:bg-blush-50 hover:text-blush-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8" data-testid="admin-orders-panel">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:max-w-md">
              <div className="rounded-[24px] border border-lav-100 bg-white p-6">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Orders</p>
                <p className="mt-2 font-display text-3xl font-semibold text-ink" data-testid="orders-count">
                  {orderStats.count}
                </p>
              </div>
              <div className="rounded-[24px] border border-lav-100 bg-white p-6">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Revenue</p>
                <p className="mt-2 font-display text-3xl font-semibold text-ink" data-testid="orders-revenue">
                  £{orderStats.revenue.toFixed(2)}
                </p>
              </div>
            </div>

            {loadingOrders ? (
              <div className="mt-6 space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-28 animate-pulse rounded-[24px] bg-blush-50" />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="mt-6 rounded-[24px] border border-dashed border-lav-300 bg-white/60 p-12 text-center">
                <Receipt className="mx-auto h-10 w-10 text-lav-500" />
                <p className="mt-4 font-display text-xl font-semibold text-ink">No orders yet</p>
                <p className="mt-1 text-sm text-muted">
                  Demo and real orders will appear here as they come in.
                </p>
              </div>
            ) : (
              <ul className="mt-6 space-y-4">
                {orders.map((o) => (
                  <li
                    key={o.id}
                    data-testid={`admin-order-row-${o.id}`}
                    className="rounded-[24px] border border-lav-100 bg-white p-5"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="font-display text-lg font-semibold text-ink">{o.id}</span>
                        <span
                          className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${
                            STATUS_STYLES[o.status] || "bg-lav-50 text-lav-700"
                          }`}
                        >
                          {o.payment === "stripe" ? "Card (Stripe)" : o.payment === "paypal" ? "PayPal" : "Demo"} · {o.status}
                        </span>
                      </div>
                      <span className="text-xs text-muted">
                        {new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <ul className="mt-3 space-y-1 text-sm text-ink/75">
                      {(o.items || []).map((it, i) => (
                        <li key={i}>
                          {it.qty} × {it.name} <span className="text-muted">(size {it.size}{it.colour ? ` · ${it.colour}` : ""})</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-lav-50 pt-3 text-sm">
                      <span className="text-muted">
                        {o.contact?.name || o.contact?.email || "Guest"} — {o.contact?.address ? `${o.contact.address}, ${o.contact.city} ${o.contact.postcode}${o.contact.country && o.contact.country !== "United Kingdom" ? `, ${o.contact.country}` : ""}` : "No address given"}
                      </span>
                      <span className="font-display text-lg font-semibold text-ink">£{o.total?.toFixed(2)}</span>
                    </div>
                    <div className="mt-3 rounded-2xl bg-cream/70 p-4" data-testid={`order-status-area-${o.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">
                          Progress:
                        </span>
                        <span
                          className="rounded-full bg-mint-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-mint-600"
                          data-testid={`order-delivery-status-${o.id}`}
                        >
                          {DELIVERY_LABELS[o.delivery_status || "confirmed"]}
                        </span>
                        {DELIVERY_STEPS.map((s) => (
                          <button
                            key={s}
                            onClick={() => updateOrderStatus(o, s)}
                            disabled={statusBusy === o.id}
                            data-testid={`order-status-button-${s}-${o.id}`}
                            aria-label={`Mark ${o.id} as ${DELIVERY_LABELS[s]}`}
                            className="rounded-full border border-lav-100 bg-white px-3 py-1.5 text-xs font-semibold text-ink transition-colors duration-200 hover:bg-blush-50 disabled:opacity-50"
                          >
                            {DELIVERY_LABELS[s]}
                          </button>
                        ))}
                      </div>
                      <input
                        value={trackingDraft[o.id] ?? o.tracking_number ?? ""}
                        onChange={(e) => setTrackingDraft((d) => ({ ...d, [o.id]: e.target.value }))}
                        placeholder="Tracking number (optional — goes into the buyer's email)"
                        data-testid={`order-tracking-input-${o.id}`}
                        className="mt-3 w-full rounded-xl border border-lav-100 bg-white px-3 py-2 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
                      />
                      <p className="mt-2 text-xs text-muted">
                        Tapping a status instantly emails the buyer their update.
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "settings" && (
          <div className="mt-8 max-w-md" data-testid="admin-settings-panel">
            <form
              onSubmit={changePass}
              data-testid="admin-passcode-form"
              className="rounded-[32px] border border-lav-100 bg-white p-7 md:p-8"
            >
              <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-ink">
                <LockKeyhole className="h-5 w-5 text-blush-600" />
                Change your passcode
              </h2>
              <p className="mt-2 text-sm text-muted">
                You'll need the new passcode next time you log in — keep it safe.
              </p>
              {[
                { key: "current", label: "Current passcode", ph: "SPARK2026" },
                { key: "next", label: "New passcode (min 6 characters)", ph: "" },
              ].map((f) => (
                <label key={f.key} className="mt-4 block">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">{f.label}</span>
                  <input
                    type="password"
                    value={passForm[f.key]}
                    onChange={(e) => setPassForm((p) => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.ph}
                    data-testid={`passcode-field-${f.key}`}
                    className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
                  />
                </label>
              ))}
              <button
                type="submit"
                disabled={changingPass}
                data-testid="passcode-change-button"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70"
              >
                {changingPass && <Loader2 className="h-4 w-4 animate-spin" />}
                {changingPass ? "Saving..." : "Save new passcode"}
              </button>
            </form>
          </div>
        )}
      </div>
    </main>
  );
}
